import React, { useEffect, useMemo, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";

import Box from "@mui/material/Box";

import Button from "@mui/material/Button";

import CircularProgress from "@mui/material/CircularProgress";

import Dialog from "@mui/material/Dialog";

import DialogActions from "@mui/material/DialogActions";

import DialogContent from "@mui/material/DialogContent";

import DialogTitle from "@mui/material/DialogTitle";

import IconButton from "@mui/material/IconButton";

import Typography from "@mui/material/Typography";

import {

  DynamicFormCompBasicJsonSchema as CommonDynamicFormCompBasicJsonSchema,

  FreyrLibraryProvider as CommonFreyrLibraryProvider,

} from "@freyr/freyr-react-common";

import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";

import { FreyrLibraryContext } from "../../context/FreyrLibraryContext";

import type { PreInitTask, PreInitWorkflowField } from "../../types/manualAssessmentWorkflow";

import {
  PRE_INIT_EMAIL_NOTIFICATION_CHECKBOX_LABEL,
  syntheticTaskDetailFromInitTasks,
  validatePreInitFormData,
} from "../../utils/manualAssessmentInitTasks";

import { taskDetailToPreInitDynamicFormConfig } from "../../utils/taskDetailToDynamicFormConfig";

import { withoutBackdropClose } from "../../utils/withoutBackdropClose";

import {
  allowTextareaEnterNewlineCapture,
  type DynamicFormRef,
} from "./ManualAssessmentForm";



export type ManualAssessmentPreInitDialogProps = Readonly<{

  open: boolean;

  initTasks: PreInitTask[];

  workflowFields?: PreInitWorkflowField[];

  submitting?: boolean;

  onClose: () => void;

  onConfirm: (formData: Record<string, unknown>) => void | Promise<void>;

}>;



/** Pre-init form field labels; avoid theme primary blue on focus. */
const PRE_INIT_FORM_LABEL_COLOR = "rgba(54, 65, 83, 1)";

const PRE_INIT_FORM_SX = {
  "& .MuiGrid-container": {
    rowGap: "16px",
    marginLeft: "-10px",
    width: "calc(100% + 20px)",
  },
  "& .MuiGrid-container > .MuiGrid-item": {
    boxSizing: "border-box",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  "& .MuiFormLabel-root, & .MuiFormLabel-root.Mui-focused, & .MuiFormLabel-root.Mui-disabled, & .MuiFormLabel-root.Mui-error":
    {
      color: `${PRE_INIT_FORM_LABEL_COLOR} !important`,
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 400,
    },
  "& .MuiFormLabel-root .MuiSvgIcon-root": {
    color: `${PRE_INIT_FORM_LABEL_COLOR} !important`,
  },
  [`& .MuiFormControlLabel-root:has(#root_openCommonNotifications) .MuiFormControlLabel-label:empty::before`]:
    {
      content: `"${PRE_INIT_EMAIL_NOTIFICATION_CHECKBOX_LABEL}"`,
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 400,
      color: PRE_INIT_FORM_LABEL_COLOR,
    },
  "& .MuiFormControlLabel-root:has(#root_openCommonNotifications)": {
    marginLeft: 0,
    marginTop: 0,
  },
  "& .MuiTooltip-tooltip": {
    whiteSpace: "pre-line",
    maxWidth: 360,
  },
} as const;

const FOOTER_BUTTON_SX = {

  textTransform: "none",

  borderRadius: "4px",

  fontFamily: '"Nunito Sans", sans-serif',

  fontSize: 14,

  lineHeight: "20px",

  fontWeight: 400,

  px: 1.5,

  py: 1,

  minWidth: 0,

  height: 36,

} as const;

const PRE_INIT_DIALOG_DEBUG_PREFIX = "[PreInitDialog]";



export default function ManualAssessmentPreInitDialog({

  open,

  initTasks,

  workflowFields = [],

  submitting = false,

  onClose,

  onConfirm,

}: ManualAssessmentPreInitDialogProps) {

  const freyrLibraryContext = React.useContext(FreyrLibraryContext);

  const formRef = useRef<DynamicFormRef>(null);

  const [formData, setFormData] = useState<Record<string, unknown>>({});



  const syntheticTaskDetail = useMemo(

    () => syntheticTaskDetailFromInitTasks(initTasks, workflowFields),

    [initTasks, workflowFields],

  );

  const dynamicFormConfig = useMemo(

    () => taskDetailToPreInitDynamicFormConfig(syntheticTaskDetail),

    [syntheticTaskDetail],

  );

  useEffect(() => {
    if (!open) return;
    const taskOwnersField = dynamicFormConfig.jsonData.find(
      (item) => item.field === "taskOwners",
    );
    const taskAssigneeField = dynamicFormConfig.jsonData.find(
      (item) => item.field === "taskAssignee",
    );
    console.info(PRE_INIT_DIALOG_DEBUG_PREFIX, "dialog opened — field config", {
      taskOwners: {
        asyncConfig: taskOwnersField?.asyncConfig,
        groupConfig: (taskOwnersField as { groupConfig?: unknown })?.groupConfig,
        multiple: taskOwnersField?.multiple,
        widget: "CustomSelectGroupWidget (GET /task-owners)",
      },
      taskAssignee: {
        asyncConfig: taskAssigneeField?.asyncConfig,
        type: taskAssigneeField?.type,
        multiple: taskAssigneeField?.multiple,
        groupConfig: (taskAssigneeField as { groupConfig?: unknown })?.groupConfig,
        widget: "CustomSelectWidget (POST /task-assignees)",
      },
      initialFormData: dynamicFormConfig.initialFormData,
    });
  }, [open, dynamicFormConfig]);



  const confirmIfValid = (data: Record<string, unknown>) => {

    if (validatePreInitFormData(dynamicFormConfig.jsonData, data)) {

      return;

    }

    void onConfirm(data);

  };



  const handleConfirm = () => {

    const latest =

      formRef.current?.getFormState() ??

      formData ??

      dynamicFormConfig.initialFormData;

    if (validatePreInitFormData(dynamicFormConfig.jsonData, latest)) {

      formRef.current?.submitForm();

      return;

    }

    void onConfirm(latest);

  };



  const handleClose = () => {

    if (!submitting) onClose();

  };



  return (

    <Dialog

      open={open}

      onClose={submitting ? undefined : withoutBackdropClose(handleClose)}

      maxWidth="md"

      fullWidth

    >

      <DialogTitle

        component="div"

        sx={{

          display: "flex",

          alignItems: "flex-start",

          justifyContent: "space-between",

          borderBottom: "1px solid #F3F4F6",

          px: 3,

          py: 2,

        }}

      >

        <Box>

          <Typography

            sx={{

              fontFamily: '"Nunito Sans", sans-serif',

              fontWeight: 700,

              fontSize: 20,

              lineHeight: "25px",

              color: "#0F172A",

            }}

          >

            Triage Task Assignment

          </Typography>

          <Typography

            sx={{

              fontFamily: '"Nunito Sans", sans-serif',

              fontWeight: 400,

              fontSize: 14,

              lineHeight: "20px",

              color: "#64748B",

            }}

          >

            Select an assignee for Identify SME Category task

          </Typography>

        </Box>

        <IconButton

          aria-label="Close Triage Task Assignment"

          onClick={handleClose}

          disabled={submitting}

          sx={{ ...DIALOG_CLOSE_BUTTON_SX, mt: 0.25 }}

        >

          <CloseIcon />

        </IconButton>

      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: "16px !important", pb: 0 }}>

        <Box
          sx={PRE_INIT_FORM_SX}
          onKeyDownCapture={allowTextareaEnterNewlineCapture}
        >

          <CommonFreyrLibraryProvider {...(freyrLibraryContext ?? {})}>

            <CommonDynamicFormCompBasicJsonSchema

              ref={formRef}

              uniqueKey={`manual-assessment-pre-init-${initTasks.length}`}

              jsonData={dynamicFormConfig.jsonData}

              initialFormData={dynamicFormConfig.initialFormData}

              onChange={(data) => {
                const next = data as Record<string, unknown>;
                setFormData(next);
              }}

              onFormSubmit={(data) => {
                confirmIfValid(data as Record<string, unknown>);
              }}

              removeButtons

            />

          </CommonFreyrLibraryProvider>

        </Box>

      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1.5 }}>

        <Button

          variant="outlined"

          onClick={handleClose}

          disabled={submitting}

          sx={{

            ...FOOTER_BUTTON_SX,

            borderColor: "#155DFC",

            color: "#155DFC",

          }}

        >

          Back

        </Button>

        <Button

          variant="contained"

          disabled={submitting}

          onClick={() => void handleConfirm()}

          startIcon={

            submitting ? <CircularProgress size={18} color="inherit" /> : undefined

          }

          sx={{

            ...FOOTER_BUTTON_SX,

            bgcolor: "#155DFC",

            "&:hover": { bgcolor: "#0F4FD8" },

          }}

        >

          Initiate

        </Button>

      </DialogActions>

    </Dialog>

  );

}


